import React from 'react';
import styled from 'styled-components';
import { Vehicle } from 'types/vehicle';
import { VehicleImageCard } from 'components/vehicle-history';
import Tooltip from 'components/Tooltip';
import VinIcon from '../icons/vin.svg';
import MileageIcon from '../icons/mileage.svg';
import SellerIcon from '../icons/seller.svg';
import PickupIcon from '../icons/pickup_instructions.svg';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  width: 360px;
  padding: 11px;
  background: #f9f9f9;
  border-radius: 10px;
  flex-shrink: 0;
`;

const VehicleIdBold = styled.span`
  font-weight: 700;
`;

const VehicleIdSlug = styled.span`
  font-weight: 400;
`;

const AwardedLabel = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: #4caf50;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 12px;
  letter-spacing: 0.3px;
`;

const VehicleTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #000;
  margin-top: 10px;
  line-height: 1.3;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  gap: 6px;
  width: 100%;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PickupInfoRow = styled(InfoRow)`
  align-items: flex-start;
`;

const InfoIcon = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 1px;
`;

const InfoContent = styled.div`
  display: flex;
`;

const PickupInfoContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const InfoLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #000;
  min-width: fit-content;
`;

const InfoValue = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #000;
  min-width: fit-content;
`;

const ReportOfSaleLink = styled.a`
  font-size: 16px;
  font-weight: 700;
  color: #225a91;
  text-decoration: none;
  min-width: fit-content;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const PickupText = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #666666;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: default;
`;

interface VehicleLeftPanelProps {
  vehicle: Vehicle & { buyer_report_of_sale?: string };
  onImageClick?: () => void;
  showAwarded?: boolean;
}

const VehicleLeftPanel = ({ vehicle, onImageClick, showAwarded = true }: VehicleLeftPanelProps) => {
  const vehicleTitle = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const sellerName = vehicle.user?.dealership_name ?? 'N/A';
  const imageUrl = vehicle.preview_image?.url;
  const pickupInstruction = vehicle.user?.seller?.pickup_instruction;
  const auctionId = vehicle.auctionSlug ? `${vehicle.auctionSlug}-${vehicle.orderIndex || '-'}` : null;

  return (
    <Panel>
      <VehicleImageCard
        imageUrl={imageUrl}
        label={auctionId && <><VehicleIdBold>Vehicle ID: </VehicleIdBold><VehicleIdSlug>{auctionId}</VehicleIdSlug></>}
        onClick={onImageClick}
        badge={showAwarded ? <AwardedLabel>Awarded</AwardedLabel> : undefined}
      />

      <VehicleTitle>{vehicleTitle}</VehicleTitle>

      <InfoList>
        <InfoRow>
          <VinIcon />
          <InfoContent>
            <InfoLabel>VIN:</InfoLabel>
            <InfoValue>{vehicle.vin}</InfoValue>
          </InfoContent>
        </InfoRow>

        <InfoRow>
          <MileageIcon />
          <InfoContent>
            <InfoLabel>{vehicle.mileage?.toLocaleString()}</InfoLabel>
            <InfoValue>/Mileage</InfoValue>
          </InfoContent>
        </InfoRow>

        <InfoRow>
          <SellerIcon />
          <InfoContent>
            <InfoLabel>{sellerName}</InfoLabel>
          </InfoContent>
        </InfoRow>

        <PickupInfoRow>
          <PickupIcon />
          <PickupInfoContent>
            <InfoContent>
              <InfoLabel>Pickup instructions</InfoLabel>
              <div style={{ flex: '1 1 100%' }} />
              {vehicle.buyer_report_of_sale && (
                <ReportOfSaleLink href={vehicle.buyer_report_of_sale} target="_blank" rel="noopener noreferrer">
                  Report of Sale
                </ReportOfSaleLink>
              )}
            </InfoContent>
            {pickupInstruction && (
              <Tooltip title={pickupInstruction} isTextOverflown placement="bottom" light>
                <PickupText>{pickupInstruction}</PickupText>
              </Tooltip>
            )}
          </PickupInfoContent>
        </PickupInfoRow>
      </InfoList>
    </Panel>
  );
};

export default VehicleLeftPanel;
